/**
 * Predictive search overlay.
 * The overlay is a cream stage under a plum strip; it fades in and out.
 * The input is an ARIA combobox; results come from the predictive-search
 * section through the Section Rendering API.
 */
(function () {
  if (customElements.get('predictive-search')) return;

  const Lumi = (window.Lumi = window.Lumi || {});
  const RECENT_KEY = 'lumi:recent-searches';
  const RECENT_MAX = 5;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.form = this.querySelector('form');
      this.input = this.querySelector('[role="combobox"]');
      this.results = this.querySelector('[data-results]');
      this.recentWrap = this.querySelector('[data-recent-searches]');
      this.recentList = this.querySelector('[data-recent-list]');
      this.status = this.querySelector('[data-search-status]');
      this.cache = new Map();
      if (!this.dialog || !this.input) return;

      this.debouncedSearch = Lumi.debounce(() => this.search(), 200);
      this.input.addEventListener('input', () => {
        this.toggleAttribute('data-has-query', this.input.value.trim().length > 0);
        if (!this.input.value.trim()) this.search();
        else this.debouncedSearch();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', () => this.remember(this.input.value));

      this.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option && this.input.value.trim()) this.remember(this.input.value);
        if (event.target.closest('[data-search-close]')) this.close();
        if (event.target.closest('[data-recent-clear]')) {
          Lumi.storage.set(RECENT_KEY, []);
          this.renderRecent();
          this.input.focus();
        }
      });
      this.addEventListener('mouseover', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.select(this.options().indexOf(option), false);
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && event.target !== this.input) {
          event.preventDefault();
          this.close();
        }
        if (Lumi.trapFocus) Lumi.trapFocus(this.dialog, event);
      });
      this.dialog.addEventListener('close', () => this.afterClose());

      // Any element with data-search-open opens the overlay.
      this.onOpenClick = (event) => {
        const trigger = event.target.closest('[data-search-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this.onOpenClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOpenClick);
    }

    /* ---------- Open and close ---------- */
    open(trigger) {
      if (this.dialog.open) return;
      this.trigger = trigger || document.activeElement;
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      Lumi.lockScroll(true);
      // Swipe down to dismiss on mobile (assets/app-shell.js).
      if (Lumi.app && Lumi.app.enableSheetSwipe) {
        Lumi.app.enableSheetSwipe(this.dialog, {
          onClose: () => this.close(),
        });
      }
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      this.renderRecent();
      this.input.focus();
      this.input.select();
      Lumi.emit('lumi:dialog:opened', { id: this.dialog.id });
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      this.closing = true;
      if (!Lumi.reducedMotion()) {
        this.dialog.classList.add('is-closing');
        window.setTimeout(() => this.dialog.close(), 200);
      } else {
        this.dialog.close();
      }
    }

    afterClose() {
      this.closing = false;
      this.dialog.classList.remove('is-closing');
      Lumi.lockScroll(false);
      this.select(-1);
      if (this.trigger && this.trigger.setAttribute) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.trigger.focus) this.trigger.focus();
      }
      Lumi.emit('lumi:dialog:closed', { id: this.dialog.id });
    }

    /* ---------- Fetching ---------- */
    async search() {
      const q = this.input.value.trim();
      if (this.controller) this.controller.abort();
      this.select(-1);

      if (!q) {
        this.results.innerHTML = '';
        this.setStatus('');
        this.renderRecent();
        return;
      }
      if (this.recentWrap) this.recentWrap.hidden = true;

      if (this.cache.has(q)) {
        this.render(this.cache.get(q));
        return;
      }

      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${Lumi.routes.predictive_search_url}?${params.toString()}`, {
          signal: this.controller.signal,
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`Predictive search failed with ${res.status}`);
        const doc = Lumi.parseHTML(await res.text());
        const fresh = doc.querySelector('[data-predictive-results]');
        const html = fresh ? fresh.outerHTML : '';
        this.cache.set(q, html);
        if (this.input.value.trim() === q) this.render(html);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('[Lumi search]', err);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      const listbox = this.results.querySelector('[role="listbox"]');
      const options = this.options();
      this.input.setAttribute('aria-controls', listbox ? listbox.id : this.results.id);
      this.input.setAttribute('aria-expanded', String(options.length > 0));
      const root = this.results.querySelector('[data-predictive-results]');
      const total = root ? parseInt(root.dataset.total || '0', 10) : 0;
      this.setStatus(this.countLabel(total));
    }

    countLabel(total) {
      const tpl = this.dataset[total === 1 ? 'countOne' : 'countOther'];
      return tpl ? tpl.replace(/\{\{\s*count\s*\}\}/, total) : String(total);
    }

    setStatus(text) {
      if (!this.status) return;
      this.status.textContent = text;
    }

    /* ---------- Keyboard ---------- */
    options() {
      const scope = this.input.value.trim() ? this.results : this.recentWrap;
      if (!scope || scope.hidden) return [];
      return [...scope.querySelectorAll('[role="option"]')];
    }

    onKeydown(event) {
      const options = this.options();
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      switch (event.key) {
        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          this.select(current + 1 >= options.length ? 0 : current + 1);
          break;
        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          this.select(current <= 0 ? options.length - 1 : current - 1);
          break;
        case 'Enter':
          if (current > -1) {
            event.preventDefault();
            if (this.input.value.trim()) this.remember(this.input.value);
            window.location.href = options[current].href;
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        default:
      }
    }

    select(index, scroll = true) {
      const options = [...this.querySelectorAll('[role="option"]')];
      const active = this.options();
      options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      const target = index > -1 ? active[index] : null;
      if (target) {
        target.setAttribute('aria-selected', 'true');
        this.input.setAttribute('aria-activedescendant', target.id);
        if (scroll) target.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.setAttribute('aria-activedescendant', '');
      }
    }

    /* ---------- Recent searches ---------- */
    remember(value) {
      const term = String(value || '').trim();
      if (!term) return;
      const list = Lumi.storage.get(RECENT_KEY, []).filter((t) => typeof t === 'string' && t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      Lumi.storage.set(RECENT_KEY, list.slice(0, RECENT_MAX));
    }

    renderRecent() {
      if (!this.recentWrap || !this.recentList) return;
      const list = Lumi.storage.get(RECENT_KEY, []).filter((t) => typeof t === 'string').slice(0, RECENT_MAX);
      const empty = this.input.value.trim().length > 0 || list.length === 0;
      this.recentWrap.hidden = empty;
      this.toggleAttribute('data-has-recent', list.length > 0);
      this.recentList.textContent = '';
      const base = (Lumi.routes && Lumi.routes.search_url) || '/search';
      list.forEach((term, i) => {
        const a = document.createElement('a');
        a.className = 'search-chip';
        a.id = `PredictiveSearchRecent${i}`;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.href = `${base}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        a.textContent = term;
        this.recentList.appendChild(a);
      });
      if (!this.input.value.trim()) {
        this.input.setAttribute('aria-controls', empty ? this.results.id : this.recentList.id);
        this.input.setAttribute('aria-expanded', String(!empty));
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
